import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isKnownNotificationKind, notificationValues } from '@entities/admin-notification';
import type { AdminNotification, WorkspaceNotificationCategory } from '@shared/api/contracts/workspace';
import { formatDateTime } from '@shared/lib/format-date-time';
import { cn } from '@shared/lib/utils';
import { Button, Skeleton, SkeletonStatus, StatusBanner, Switch, Tabs, TabsList, TabsTrigger, TypographyMuted } from '@shared/ui';

const CATEGORIES = ['all', 'withdrawals', 'accounts', 'security', 'projects', 'money', 'system'] as const;

const severityClass: Record<AdminNotification['severity'], string> = {
  INFO: 'bg-muted',
  WARNING: 'bg-pending',
  CRITICAL: 'bg-fail',
};

interface NotificationFeedProps {
  items: AdminNotification[];
  loading: boolean;
  error: boolean;
  category: (typeof CATEGORIES)[number];
  unreadOnly: boolean;
  pendingNew: number;
  unverifiedCount: number;
  sound: boolean;
  onCategory: (category: (typeof CATEGORIES)[number]) => void;
  onUnreadOnly: (value: boolean) => void;
  onReadAll: () => void;
  onLoadMore: () => void;
  hasMore: boolean;
  onShowNew: () => void;
  onOpen: (item: AdminNotification) => void;
  onSound: (value: boolean) => void;
}

const relativeLabel = (iso: string, language: string, now: number): string => {
  const minutes = Math.round((new Date(iso).getTime() - now) / 60000);
  const format = new Intl.RelativeTimeFormat(language, { numeric: 'auto' });
  if (Math.abs(minutes) < 60) return format.format(minutes, 'minute');
  const hours = Math.round(minutes / 60);
  if (Math.abs(hours) < 24) return format.format(hours, 'hour');
  return format.format(Math.round(hours / 24), 'day');
};

export function NotificationFeed(props: NotificationFeedProps) {
  const { t, i18n } = useTranslation('cabinet');
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 30000);
    return () => window.clearInterval(timer);
  }, []);

  return (
    <section className="flex max-h-[min(36rem,60dvh)] min-h-[16rem] min-w-0 flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-border px-3 py-3 sm:px-4">
        <p className="text-sm font-semibold">{t('workspace.notifications')}</p>
        <Button type="button" size="sm" variant="outline" onClick={props.onReadAll}>{t('workspace.readAll')}</Button>
        <div className="ml-auto flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-muted-foreground">
            <Switch checked={props.unreadOnly} onCheckedChange={(checked) => props.onUnreadOnly(checked === true)} />
            {t('workspace.unreadOnly')}
          </label>
          <label className="flex items-center gap-2 text-sm text-muted-foreground">
            {t('workspace.sound')}
            <Switch checked={props.sound} onCheckedChange={(checked) => props.onSound(checked === true)} />
          </label>
        </div>
      </div>
      {props.unverifiedCount > 0 ? (
        <StatusBanner className="mx-3 mt-3" variant="pending">
          <span className="block">{t('workspace.unverified', { count: props.unverifiedCount })}</span>
          <span className="mt-1 block text-xs">{t('workspace.unverifiedHint')}</span>
        </StatusBanner>
      ) : null}
      <Tabs className="border-b border-border px-3 py-2" value={props.category} onValueChange={(value) => props.onCategory(value as WorkspaceNotificationCategory | 'all')}>
        <TabsList className="h-auto w-full flex-wrap justify-start">
          {CATEGORIES.map((category) => (
            <TabsTrigger key={category} value={category}>{t(`workspace.categories.${category}`)}</TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
      {props.error ? <StatusBanner className="mx-3 mt-3" variant="destructive">{t('workspace.loadError')}</StatusBanner> : null}
      {props.pendingNew > 0 ? (
        <button type="button" className="mx-3 mt-2 rounded-lg bg-muted px-3 py-1.5 text-left text-sm font-medium" onClick={props.onShowNew}>{t('workspace.newItems', { count: props.pendingNew })}</button>
      ) : null}
      <div id="workspace-feed" className="min-h-0 flex-1 space-y-1.5 overflow-y-auto p-3">
        {props.loading && !props.items.length ? (
          <SkeletonStatus className="space-y-2" label={t('workspace.loading')}>
            {Array.from({ length: 6 }).map((_, index) => (
              <div key={index} className="flex gap-3 rounded-xl border border-border px-3 py-2.5">
                <Skeleton className="mt-1 size-2 rounded-full" />
                <div className="min-w-0 flex-1 space-y-2">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-3 w-24" />
                </div>
              </div>
            ))}
          </SkeletonStatus>
        ) : null}
        {!props.loading && !props.items.length ? <TypographyMuted className="block py-10 text-center">{t('workspace.feedEmpty')}</TypographyMuted> : null}
        {props.items.map((item) => (
          <button
            key={item.id}
            type="button"
            className={cn(
              'flex w-full min-w-0 gap-3 rounded-xl border border-transparent px-3 py-2.5 text-left transition-colors duration-fast ease-standard hover:bg-muted/60',
              item.isRead ? 'opacity-80' : 'bg-muted/40',
            )}
            onClick={() => props.onOpen(item)}
          >
            <span className={cn('mt-1.5 size-2 shrink-0 rounded-full', severityClass[item.severity])} />
            <span className="min-w-0 flex-1">
              <span className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">{t(`workspace.categories.${item.category}`)}</span>
                {item.repeatCount > 1 ? <span className="text-xs text-muted-foreground">×{item.repeatCount}</span> : null}
                <span className="ml-auto shrink-0 text-xs text-muted-foreground" title={formatDateTime(item.lastOccurredAt, i18n.language)}>
                  {relativeLabel(item.lastOccurredAt, i18n.language, now)}
                </span>
              </span>
              <span className="mt-0.5 block text-sm leading-5">
                {isKnownNotificationKind(item.kind)
                  ? t(`workspace.kinds.${item.kind}`, notificationValues(item))
                  : item.fallbackText}
              </span>
            </span>
          </button>
        ))}
      </div>
      {props.hasMore ? (
        <div className="border-t border-border p-3">
          <Button type="button" variant="outline" className="w-full" onClick={props.onLoadMore}>{t('workspace.loadMore')}</Button>
        </div>
      ) : null}
    </section>
  );
}
